"use client";

import { useState } from "react";
import Link from "next/link";
import { useQuery } from "@tanstack/react-query";
import { Award, BadgeCheck, ExternalLink, MessageSquare } from "lucide-react";
import { HoverCard, HoverCardContent, HoverCardTrigger } from "@/app/_module/components/ui/hover-card";
import { Avatar, AvatarFallback, AvatarImage } from "@/app/_module/components/ui/avatar";
import { Button } from "@/app/_module/components/ui/button";
import { forumService, ForumAuthor, formatScore, timeAgo, lpProfileUrl, forumPath } from "./ForumService";
import { LpVerifiedBadge, StaffBadge } from "./ForumBadges";

// Author name + avatar with a lazy-loaded karma hovercard (profile fetched
// only when the card opens). LP-verified authors get the blue check inline
// and a "View agent profile" button in the card.
export function AuthorName({ author, showAvatar = false }: { author: ForumAuthor; showAvatar?: boolean }) {
  const [open, setOpen] = useState(false);

  const { data: profile } = useQuery({
    queryKey: ["forum", "user", author.id],
    queryFn: () => forumService.getUserProfile(author.id),
    enabled: open,
    staleTime: 5 * 60_000,
  });

  const initials = (author.name || "A")
    .split(" ")
    .map((p) => p[0])
    .slice(0, 2)
    .join("")
    .toUpperCase();

  return (
    <HoverCard openDelay={350} closeDelay={100} onOpenChange={setOpen}>
      <HoverCardTrigger asChild>
        <span className="inline-flex items-center gap-1 cursor-pointer group" onClick={(e) => e.stopPropagation()}>
          {showAvatar && (
            <Avatar className="h-4 w-4">
              <AvatarImage src={author.avatarUrl || (author.isStaff ? "/favicon.png" : undefined)} alt="" />
              <AvatarFallback className="text-[8px]">{initials}</AvatarFallback>
            </Avatar>
          )}
          <span className="text-xs font-medium text-foreground/80 group-hover:text-primary group-hover:underline">
            {author.name}
          </span>
          {author.lpSlug && <LpVerifiedBadge slug={author.lpSlug} />}
          {author.isStaff && <StaffBadge />}
        </span>
      </HoverCardTrigger>
      <HoverCardContent className="w-72 p-3" side="top" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start gap-3">
          <Avatar className="h-10 w-10">
            <AvatarImage src={author.avatarUrl || (author.isStaff ? "/favicon.png" : undefined)} alt="" />
            <AvatarFallback>{initials}</AvatarFallback>
          </Avatar>
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-1.5">
              <span className="text-sm font-semibold truncate">{author.name}</span>
              {author.isStaff && <StaffBadge />}
              {(profile?.lpSlug || author.lpSlug) && (
                <span className="inline-flex items-center gap-0.5 text-[10px] font-medium text-sky-600 bg-sky-50 dark:bg-sky-950 rounded px-1 py-0.5">
                  <BadgeCheck className="h-3 w-3" /> Verified
                </span>
              )}
            </div>
            <div className="mt-1 flex items-center gap-3 text-xs text-muted-foreground">
              <span className="inline-flex items-center gap-1 font-semibold text-foreground/80">
                <Award className="h-3 w-3 text-primary" />
                {formatScore(profile?.karma ?? author.karma)} Rep
              </span>
              {profile && (
                <span className="inline-flex items-center gap-1">
                  <MessageSquare className="h-3 w-3" />
                  {formatScore(profile.postCount + profile.commentCount)} contributions
                </span>
              )}
            </div>
            {profile && (
              <div className="mt-0.5 text-[10px] text-muted-foreground">
                Joined {new Date(profile.joinedAt).toLocaleDateString(undefined, { month: "short", year: "numeric" })}
              </div>
            )}
          </div>
        </div>

        {profile?.recentPosts && profile.recentPosts.length > 0 && (
          <div className="mt-2 border-t pt-2 space-y-1">
            {profile.recentPosts.slice(0, 3).map((p) => (
              <Link
                key={p.slug}
                href={forumPath(`/t/${p.slug}`)}
                className="block text-xs text-foreground/80 hover:text-primary truncate"
              >
                <span className="text-muted-foreground">{timeAgo(p.createdAt)} ·</span> {p.title}
              </Link>
            ))}
          </div>
        )}

        {(profile?.lpSlug || author.lpSlug) && (
          <Button
            asChild
            size="sm"
            variant="outline"
            className="mt-2 h-7 w-full text-xs gap-1 border-sky-200 text-sky-700 hover:bg-sky-50 hover:text-sky-800"
          >
            <a href={lpProfileUrl((profile?.lpSlug || author.lpSlug)!)} target="_blank" rel="noopener noreferrer">
              <BadgeCheck className="h-3.5 w-3.5" />
              View agent profile
              <ExternalLink className="h-3 w-3 ml-auto" />
            </a>
          </Button>
        )}
      </HoverCardContent>
    </HoverCard>
  );
}
